import { useState } from 'react';
import { Send, X, Upload, CheckCircle, Mail } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { toast } from 'sonner';
import api from '../../../api/clients';

interface EnviarEmailModalProps {
  entrega: any;
  onClose: () => void;
  onSuccess: () => void;
}

export function EnviarEmailModal({ entrega, onClose, onSuccess }: EnviarEmailModalProps) {
  const [loading, setLoading] = useState(false);
  const [asunto, setAsunto] = useState(
    ` ¡Tu compra ha sido confirmada! - ${entrega.numeroEnvio}`
  );
  const [mensaje, setMensaje] = useState(
    `Te confirmamos que tu compra ha sido entregada correctamente.\n\n` +
    ` Número de envío: ${entrega.numeroEnvio}\n` +
    ` Fecha de entrega: ${new Date(entrega.fechaEntrega).toLocaleDateString('es-AR')}\n\n` +
    `Podés consultar la factura desde el botón que encontrarás más abajo.\n\n` +
    `¡Gracias por confiar en Mi Tecno Hogar!\n\n` +
    `Saludos cordiales,\n` +
    `El equipo de Mi Tecno Hogar`
  );
  const [facturaFile, setFacturaFile] = useState<File | null>(null);
  const [facturaUrl, setFacturaUrl] = useState(entrega.facturaUrl || '');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Solo se permiten archivos PDF');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('El archivo no puede superar los 5MB');
        return;
      }
      setFacturaFile(file);
      toast.success(`Archivo seleccionado: ${file.name}`);
    }
  };

  const handleEnviar = async () => {
    if (!asunto.trim() || !mensaje.trim()) {
      toast.error('Completa el asunto y el mensaje');
      return;
    }

    if (!entrega.clienteEmail) {
      toast.error('El cliente no tiene email registrado');
      return;
    }

    setLoading(true);
    try {
      // Simular envío de email
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Guardar factura si se subió
      if (facturaFile) {
        const mockUrl = `/facturas/factura-${entrega.id}.pdf`;
        await api.put(`/confirmaciones/entregas/${entrega.id}/factura`, { facturaUrl: mockUrl });
        setFacturaUrl(mockUrl);
      }

      // Enviar email
      await api.post(`/confirmaciones/entregas/${entrega.id}/email`, {
        asunto,
        mensaje,
        adjuntarFactura: !!facturaFile || !!facturaUrl,
      });

      toast.success('Email enviado exitosamente');
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al enviar el email');
    } finally {
      setLoading(false);
    }
  };

  const clienteEmail = entrega.clienteEmail || 'Sin email registrado';
  const tieneEmail = !!entrega.clienteEmail;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Send size={20} className="text-blue-600" />
              Enviar Confirmación
            </h2>
            <p className="text-sm text-gray-500">
              {entrega.numeroEnvio} - {entrega.clienteNombre}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destinatario
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  value={`${entrega.clienteNombre} <${clienteEmail}>`}
                  disabled
                  className={`bg-gray-50 ${!tieneEmail ? 'border-yellow-300' : ''}`}
                />
              </div>
              {tieneEmail ? (
                <div className="p-2 bg-green-100 rounded-full" title="Email válido">
                  <Mail size={18} className="text-green-600" />
                </div>
              ) : (
                <div className="p-2 bg-yellow-100 rounded-full" title="Sin email">
                  <X size={18} className="text-yellow-600" />
                </div>
              )}
            </div>
            {!tieneEmail && (
              <p className="text-xs text-yellow-600 mt-1">
                El cliente no tiene email registrado en este envío
              </p>
            )}
            {tieneEmail && (
              <p className="text-xs text-green-600 mt-1">
                Email válido: {clienteEmail}
              </p>
            )}
          </div>

          {/* Asunto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asunto *
            </label>
            <Input
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              placeholder="Asunto del email"
            />
          </div>

          {/* Mensaje */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mensaje *
            </label>
            <textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Escribe el mensaje para el cliente..."
            />
          </div>

          {/* Adjuntar factura */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjuntar factura (PDF)
            </label>
            <div className="flex items-center gap-3">
              <label className={`flex-1 cursor-pointer ${!tieneEmail ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
                  <Upload size={18} className="text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {facturaFile ? facturaFile.name : 'Seleccionar archivo PDF'}
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={!tieneEmail}
                />
              </label>
              {facturaFile && (
                <button
                  onClick={() => setFacturaFile(null)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            {facturaUrl && !facturaFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle size={16} />
                Factura adjuntada: {facturaUrl.split('/').pop()}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex gap-3">
          <Button
            onClick={handleEnviar}
            disabled={loading || !tieneEmail}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                Enviando...
              </span>
            ) : (
              <>
                <Send size={18} />
                Enviar Email
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}